import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import LogoutIcon from '@mui/icons-material/Logout';
import {
  useChangeSubscriptionMutation,
  useSubscriptionMutation,
  useSubscriptionQuery,
} from '../shared/queries';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { SubscriptionChangeDialog } from './subscription-change';
import {
  PRICING_PLANS,
  ANNUAL_EXPERT_PRICES,
  annualExpertPriceLabel,
  BillingPeriod,
  shouldShowPlan,
  SubscriptionPlans,
  SubscriptionPlansEnum,
} from '../features/subscription/entity';
import PageHeader from '../features/subscription/components/PageHeader';
import PricingCardItem from '../features/subscription/components/PricingCardItem';
import SubscriptionInfo from '../features/subscription/components/SubscriptionInfo';
import ErrorMessage from '../features/subscription/components/ErrorMessage';

const useSubscriptionPageData = () => {
  const { mutate: subscribeMutation, error, isPending: isCreating } = useSubscriptionMutation();
  const {
    mutate: changeSubscriptionMutation,
    error: changeError,
    isPending: isChanging,
  } = useChangeSubscriptionMutation();
  const [searchParams] = useSearchParams();
  const { data: subscription } = useSubscriptionQuery(5000);
  const { user, logout } = useAuth0();

  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<BillingPeriod>('MONTHLY');
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlans | null>(null);

  const plan = searchParams.get('plan') as SubscriptionPlans | null;
  const action = searchParams.get('action');
  const isChangingSubscription = action === 'change';
  const urlCurrency = (searchParams.get('currency') as 'CZK' | 'EUR' | null) ?? 'CZK';
  const [selectedCurrency, setSelectedCurrency] = useState<'CZK' | 'EUR'>(urlCurrency);
  const currency: 'CZK' | 'EUR' =
    (subscription?.currency as 'CZK' | 'EUR' | undefined) ?? selectedCurrency;

  const subscriptionInfo = useMemo(() => {
    if (!subscription) {
      return null;
    }

    const today = dayjs();
    const endDate = subscription?.endDate ? dayjs(subscription.endDate) : null;
    const isExpired =
      subscription.status === 'EXPIRED' || (endDate ? !endDate.isAfter(today) : false);

    return {
      plan: subscription.plan,
      billingPeriod: subscription.billingPeriod,
      endDate: subscription.endDate,
      status: subscription.status,
      isExpired,
      isActive:
        !isExpired && (subscription.status === 'ACTIVE' || subscription.status === 'CANCELLED'),
    };
  }, [subscription]);

  const annualLocked =
    subscription?.billingPeriod === 'YEARLY' &&
    !!subscriptionInfo?.isActive &&
    !subscriptionInfo.isExpired;
  const handlePlanSelection = (
    newPlan: SubscriptionPlans,
    billingPeriod: BillingPeriod = 'MONTHLY',
  ) => {
    if (annualLocked) {
      return;
    }
    setSelectedPeriod(billingPeriod);
    const planConfig = PRICING_PLANS[newPlan];
    const price =
      billingPeriod === 'YEARLY'
        ? ANNUAL_EXPERT_PRICES[currency]
        : currency === 'EUR'
          ? planConfig.priceValueEur
          : planConfig.priceValue;

    if (
      subscriptionInfo?.isActive &&
      !subscriptionInfo.isExpired &&
      subscription &&
      (subscription.plan !== newPlan || (subscription.billingPeriod ?? 'MONTHLY') !== billingPeriod)
    ) {
      setSelectedPlan(newPlan);
      setChangeDialogOpen(true);
    } else if (!subscriptionInfo?.isActive) {
      subscribeMutation({
        currency,
        plan: newPlan,
        billingPeriod,
        price,
        status: 'PENDING',
      });
    }
  };

  const handleConfirmChange = () => {
    if (selectedPlan) {
      const planConfig = PRICING_PLANS[selectedPlan];
      const price =
        selectedPeriod === 'YEARLY'
          ? ANNUAL_EXPERT_PRICES[currency]
          : currency === 'EUR'
            ? planConfig.priceValueEur
            : planConfig.priceValue;
      changeSubscriptionMutation({
        currency,
        plan: selectedPlan,
        billingPeriod: selectedPeriod,
        price,
        status: 'PENDING',
      });
      setChangeDialogOpen(false);
      setSelectedPlan(null);
    }
  };

  const handleCloseDialog = () => {
    setChangeDialogOpen(false);
    setSelectedPlan(null);
  };

  const handleLogout = () => {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('@@auth0spajs@@')) {
        localStorage.removeItem(key);
      }
    });

    logout({
      logoutParams: {
        returnTo: `${window.location.origin}/hairdresser`,
      },
    });
  };

  return {
    user,
    subscription,
    subscriptionInfo,
    error: changeError ?? error,
    annualLocked,
    isPending: isCreating || isChanging,
    selectedPeriod,
    plan,
    currency,
    selectedCurrency,
    setSelectedCurrency,
    isChangingSubscription,
    changeDialogOpen,
    selectedPlan,
    handlePlanSelection,
    handleConfirmChange,
    handleCloseDialog,
    handleLogout,
  };
};

export const SubscriptionPage = () => {
  const {
    user,
    subscription,
    subscriptionInfo,
    error,
    annualLocked,
    isPending,
    selectedPeriod,
    plan,
    currency,
    selectedCurrency,
    setSelectedCurrency,
    isChangingSubscription,
    changeDialogOpen,
    selectedPlan,
    handlePlanSelection,
    handleConfirmChange,
    handleCloseDialog,
    handleLogout,
  } = useSubscriptionPageData();
  const navigate = useNavigate();

  const hasDeletedAccountError = !!(error && error.code === 410);
  const actualSubscription = subscription ?? undefined;

  const showProPlan = shouldShowPlan(
    SubscriptionPlansEnum.PRO,
    actualSubscription,
    plan,
    isChangingSubscription,
    hasDeletedAccountError,
  );
  const showExpertPlan = shouldShowPlan(
    SubscriptionPlansEnum.EXPERT,
    actualSubscription,
    plan,
    isChangingSubscription,
    hasDeletedAccountError,
  );

  if (subscriptionInfo?.isActive && !isChangingSubscription) {
    return <Navigate to="/hairdresser" replace />;
  }

  return (
    <Stack
      spacing={3}
      alignItems="center"
      justifyContent="center"
      sx={{
        width: { xs: '100%', md: '80vw' },
        minWidth: 0,
        mx: { xs: 'auto', md: 0 },
        boxSizing: 'border-box',
        position: 'relative',
        left: { xs: 0, md: '50%' },
        transform: { xs: 'none', md: 'translateX(-50%)' },
        px: { xs: 2, md: 3 },
        py: { xs: 3, md: 5 },
      }}
    >
      {subscriptionInfo?.isActive && (
        <Button variant="outlined" onClick={() => navigate('/hairdresser', { replace: true })}>
          Zpět do aplikace
        </Button>
      )}
      {isChangingSubscription ? (
        <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ maxWidth: 760 }}>
          <Typography variant="overline" color="primary" fontWeight={800} letterSpacing={2}>
            TARIFY KADEŘNÍK+
          </Typography>
          <Typography
            component="h1"
            variant="h3"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.75rem' },
              letterSpacing: '-0.04em',
            }}
          >
            Více času na to, co milujete
          </Typography>
          <Typography color="text.secondary">
            Vyberte si výbavu pro svůj salon. S ročním Expertem získáte všechny funkce za výhodnější
            cenu.
          </Typography>
          <Button variant="outlined" size="small" startIcon={<LogoutIcon />} onClick={handleLogout}>
            Odhlásit se
          </Button>
        </Stack>
      ) : (
        <PageHeader user={user} onLogout={handleLogout} />
      )}
      <ErrorMessage errorCode={error?.code ?? 0} />
      {!subscription && (
        <ToggleButtonGroup
          value={selectedCurrency}
          exclusive
          size="small"
          onChange={(_, value) => {
            if (value) {
              setSelectedCurrency(value);
            }
          }}
          aria-label="currency"
        >
          <ToggleButton value="CZK" aria-label="CZK">
            Kč
          </ToggleButton>
          <ToggleButton value="EUR" aria-label="EUR">
            €
          </ToggleButton>
        </ToggleButtonGroup>
      )}
      {subscriptionInfo && (
        <SubscriptionInfo
          subscriptionInfo={subscriptionInfo}
          isChangingSubscription={isChangingSubscription}
        />
      )}
      {annualLocked && (
        <Alert severity="info">
          Roční Expert je zaplacený do {dayjs(subscription?.endDate).format('D. M. YYYY')}. Během
          této doby jej nelze změnit na PRO ani ukončit. Obnovení na další rok lze vypnout v
          profilu.
        </Alert>
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{
          width: '100%',
          justifyContent: 'center',
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        {!annualLocked && !hasDeletedAccountError && (
          <PricingCardItem
            planType={SubscriptionPlansEnum.EXPERT}
            billingPeriod="YEARLY"
            active={false}
            disabled={isPending}
            onSelect={handlePlanSelection}
            currency={currency}
          />
        )}
        {!annualLocked && (showExpertPlan || !subscription) && (
          <PricingCardItem
            planType={SubscriptionPlansEnum.EXPERT}
            active={
              !!(
                subscriptionInfo?.isActive &&
                !subscriptionInfo.isExpired &&
                subscription?.plan === SubscriptionPlansEnum.EXPERT &&
                subscription.billingPeriod !== 'YEARLY'
              )
            }
            disabled={isPending}
            onSelect={handlePlanSelection}
            currency={currency}
          />
        )}
        {!annualLocked && (showProPlan || !subscription) && (
          <PricingCardItem
            planType={SubscriptionPlansEnum.PRO}
            active={
              !!(
                subscriptionInfo?.isActive &&
                !subscriptionInfo.isExpired &&
                subscription?.plan === SubscriptionPlansEnum.PRO
              )
            }
            disabled={isPending}
            onSelect={handlePlanSelection}
            currency={currency}
          />
        )}
      </Stack>
      {!annualLocked && !hasDeletedAccountError && (
        <Stack spacing={1} sx={{ maxWidth: 900, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Roční EXPERT se platí předem na celý rok. Během zaplaceného roku jej nelze ukončit ani
            změnit na PRO (Basic). Automatické SMS se platí zvlášť podle počtu SMS částí.
          </Typography>
          {currency === 'EUR' && (
            <Typography variant="caption" color="text.secondary">
              Měsíční přepočet ročního tarifu je zaokrouhlený. Celková roční platba je 148 €.
            </Typography>
          )}
          {!subscription && (
            <Typography variant="body2" color="text.secondary">
              Měsíční tarify pro nové zákazníky: prvních 14 dní za{' '}
              {currency === 'EUR' ? '1 €' : '1 Kč'}.
            </Typography>
          )}
          <Typography variant="body2">
            Potřebujete poradit s výběrem?{' '}
            <Button href="mailto:podpora@kadernikplus.cz" size="small">
              Napište nám
            </Button>
          </Typography>
        </Stack>
      )}

      {subscription && selectedPlan && selectedPeriod === 'YEARLY' && (
        <Dialog open={changeDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Přejít na roční Expert</DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <Typography>
                Zaplatíte {annualExpertPriceLabel(currency)} za celý rok. Import kontaktů v hodnotě{' '}
                {currency === 'EUR' ? '28 €' : '599 Kč'} získáte zdarma.
              </Typography>
              <Typography>
                Zbývající zaplacená hodnota aktuálního tarifu se převede na bonusové dny. Nový tarif
                se aktivuje po zaplacení.
              </Typography>
              <Alert severity="info">
                Během zaplaceného roku nelze tarif ukončit ani změnit na PRO. Automatické obnovení
                lze vypnout v profilu.
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Zpět</Button>
            <Button variant="contained" disabled={isPending} onClick={handleConfirmChange}>
              Pokračovat k platbě {annualExpertPriceLabel(currency)}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {subscription && selectedPlan && selectedPeriod === 'MONTHLY' && (
        <SubscriptionChangeDialog
          open={changeDialogOpen}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmChange}
          isTrial={
            subscription.status === 'ACTIVE' &&
            subscription.billingPeriod !== 'YEARLY' &&
            Number(subscription.price) === 100 &&
            dayjs(subscription.endDate).isAfter(dayjs())
          }
          currentPlan={subscription.plan as SubscriptionPlans}
          newPlan={selectedPlan}
          endDate={subscription.endDate}
          currency={currency}
        />
      )}
    </Stack>
  );
};
